import { takeEvery, put, all } from 'redux-saga/effects';

import { loaderDecorator } from './decorators/loader-decorator';

import * as types from '../actions/user-activation/types';
import * as actions from '../actions/user-activation';
import * as authUserActions from '../actions/auth-user';

import httpStatusCodes from '../constants/http-status-codes';
import { userActivationWorkflowState } from '../constants/user-activation';
import applicationStatuses from '../constants/application-statuses';

import apiFetcher from '../tools/api-fetcher';
import handleError from '../tools/error-helper';

function* sendUserActivationRequest({ email }) {
  yield put(actions.setUserActivationRequestFailedCode(undefined));
  yield put(actions.setUserActivationRequestFailedReason(undefined));

  try {
    yield apiFetcher.post('user-activation-requests', { email });
    yield put(actions.setUserActivationRequestHasBeenSentToEmail(email));
  } catch (exception) {
    switch (exception.response && exception.response.status) {
      case httpStatusCodes.NotFound:
      case httpStatusCodes.Gone:
        yield put(
          actions.setUserActivationRequestFailedCode(exception.response.status)
        );
        yield put(
          actions.setUserActivationRequestFailedReason(
            exception.response.statusText
          )
        );
        break;
      default:
        handleError(exception);
        break;
    }
  }
}

function* validateUserActivationCode({ code }) {
  try {
    yield apiFetcher.head(`user-activation-requests/${code}`);
    yield put(
      actions.setUserActivationWorkflowState(
        userActivationWorkflowState.codeIsVerified
      )
    );
  } catch (exception) {
    switch (exception.response && exception.response.status) {
      case httpStatusCodes.NotFound: {
        yield put(
          actions.setUserActivationWorkflowState(
            userActivationWorkflowState.codeIsInvalid
          )
        );
        break;
      }
      case httpStatusCodes.Gone: {
        yield put(
          actions.setUserActivationWorkflowState(
            userActivationWorkflowState.codeIsAlreadyUsed
          )
        );
        break;
      }
      default: {
        handleError(exception);
        break;
      }
    }
  }
}

function* activateUserByCode({ code }) {
  try {
    yield put(
      actions.setUserActivationWorkflowState(
        userActivationWorkflowState.codeSentToActivate
      )
    );

    yield put(authUserActions.setLoading(true));
    const { data } = yield apiFetcher.put(`user-activation-requests/${code}`);
    yield put(authUserActions.setUserFromAuthData(data));
    yield put(authUserActions.setLoading(false));

    yield fetchUserApplicationIdForRedirect();
    yield put(
      actions.setUserActivationWorkflowState(
        userActivationWorkflowState.userIsActivated
      )
    );
  } catch (exception) {
    yield put(authUserActions.setLoading(false));
    switch (exception.response && exception.response.status) {
      case httpStatusCodes.NotFound: {
        yield put(
          actions.setUserActivationWorkflowState(
            userActivationWorkflowState.codeIsInvalid
          )
        );
        break;
      }
      case httpStatusCodes.Gone: {
        yield put(
          actions.setUserActivationWorkflowState(
            userActivationWorkflowState.codeIsAlreadyUsed
          )
        );
        break;
      }
      default: {
        handleError(exception);
        break;
      }
    }
  }
}

function* fetchUserApplicationIdForRedirect() {
  const { data } = yield apiFetcher
    .auth()
    .get(
      `candidates/current/applications?statuses=${applicationStatuses.incomplete}&statuses=${applicationStatuses.active}&pageSize=1&pageNumber=1`
    );
  if (data.availableResultsCount > 0) {
    yield put(
      actions.setUserActivationIdForRedirect(data.results[0].applicationId)
    );
  }
}

function takeEveryWithLoading(type, targetFunction, loader) {
  return takeEvery(type, loaderDecorator(targetFunction, loader));
}

export default function* userActivationSaga() {
  yield all([
    takeEveryWithLoading(
      types.USER_ACTIVATION_SEND_USER_ACTIVATION_REQUEST,
      sendUserActivationRequest,
      actions.setUserActivationRequestLoading
    ),
    takeEveryWithLoading(
      types.USER_ACTIVATION_VALIDATE_USER_ACTIVATION_CODE,
      validateUserActivationCode,
      actions.setConfirmUserActivationLoading
    ),
    takeEveryWithLoading(
      types.USER_ACTIVATION_ACTIVATE_USER_BY_CODE,
      activateUserByCode,
      actions.setConfirmUserActivationLoading
    ),
    takeEvery(
      types.USER_ACTIVATION_FETCH_USER_APPLICATION_ID_FOR_REDIRECT,
      fetchUserApplicationIdForRedirect
    )
  ]);
}



// WEBPACK FOOTER //
// ./src/sagas/user-activation.js